import { UnfoldIcon } from 'components/icons'
import Select from 'components/inputs/select'
import classNames from 'helpers/class-names'
import { isFunction } from 'helpers/data'
import useIsSSR from 'hooks/use-is-ssr'
import useIsTouchDevice from 'hooks/use-is-touch-device'
import {
  ComponentProps,
  Dispatch,
  FC,
  ReactNode,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { Combobox as ComboboxHeadlessUI } from '@headlessui/react'
import { ComboboxItem } from './combobox-item'
import { ComboboxItems } from './combobox-items'
import { HeaderOption } from './header-option'
import { Option } from './option'
import { SelectedCheckmark } from './selected-checkmark'
import { ComboboxOption, ComboboxOptions, isOptionGroupGuard } from './types'
import { useFilteredOptions } from './use-filtered-options'

export type CustomItemComponent = FC<{
  selected: boolean
  active: boolean
  id: number | string
  highlights: string[]
}>

export type ComboboxProps = {
  searchPlaceholder?: string
  setSelected: Dispatch<ComboboxOption>
  selected?: ComboboxOption
  options: ComboboxOptions
  label?: string
  labelClassName?: string
  id: string
  name: string
  children?: (selected: ComboboxOption) => ReactNode
  relative?: boolean
  className?: string
  fallBackOptionValue?: (option: ComboboxOption) => string
  defaultValue?: string
  hideDefaultOption?: boolean
  selectRef?: Ref<any>
  onFocus?: any
  onBlur?: any
  setHovered?: Dispatch<number>
  OptionComponent?: CustomItemComponent
  ButtonComponent?: FC<{ id: number | string; options?: ComboboxOptions }>
  UnfoldComponent?: FC<{ open: boolean; disabled: boolean }>
  cyDataValue?: string
  disabled?: boolean
  showSearchMin?: number
  hideSearch?: boolean
  showCheckmark?: boolean
  optionIdsToOmit?: Array<number | string>
  searchPlaceholderClassName?: string
  optionWrapperClassName?: string
}

/**
 * Dropdown with integrated search. Search will automatically be enabled if there are 5 or more options
 * @param defaultValue - An option that has a null id. Will be added to the beginning of the regular
 * options list and set as selected, if no selection is provided. The defaultValue option functions
 * like any other option - it will appear at the top of the list if something else is selected and it
 * is a valid selection
 * @param selected - The option that should be selected. The client is responsible for the selection method.
 * Since this is the actual selected option, not just a reference to an item in the list, you can use an option
 * that does not exist in the regular list to provide a stand-in selection before the user has taken an action.
 * E.g: options.find((option) => option.id === tool.uuid) || {id: null, value: 'Select a tool'}
 */
export const Combobox: FC<ComboboxProps> = ({
  searchPlaceholder = 'Search...',
  setSelected,
  selected,
  label,
  options: _options,
  labelClassName = '',
  id,
  name,
  children,
  relative = true,
  className,
  fallBackOptionValue,
  defaultValue,
  hideDefaultOption = false,
  selectRef,
  onFocus,
  onBlur,
  setHovered,
  OptionComponent = null,
  ButtonComponent = null,
  UnfoldComponent = null,
  cyDataValue = null,
  disabled = false,
  showSearchMin = 20,
  hideSearch = false,
  showCheckmark = true,
  optionIdsToOmit = [],
  searchPlaceholderClassName,
  optionWrapperClassName,
}) => {
  // If server-side rendered, show fallback input checkbox.
  const isTouchDevice = useIsTouchDevice()
  const isSSR = useIsSSR()

  const [search, setSearch] = useState<string | null>()
  const options = useFilteredOptions({
    options: _options,
    defaultValue,
    search,
  })

  const showSearch = useMemo(
    () => !hideSearch && _options.length >= showSearchMin,
    [_options.length, hideSearch, showSearchMin],
  )

  const inputRef = useRef(null)

  const selectClasses = useMemo(
    () =>
      classNames(
        'relative w-full mt-1 py-2 pl-3 pr-10',
        'text-left text-gray bg-white font-body',
        'dark:bg-gray-500 dark:border-gray-700',
        'rounded-lg shadow-sm border border-gray-200',
        'cursor-default',
        'focus:outline-none focus:border-info',
        'focus:ring-2 focus:ring-info/50',
        className,
      ),
    [className],
  )

  const [isOpen, setIsOpen] = useState<boolean>(false)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(0),
      flip(),
      shift(),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          })
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ])

  useEffect(() => {
    if (isOpen) {
      onFocus?.()
    }
    if (!isOpen) {
      setSearch('')
      setHovered?.(null)
      onBlur?.()
    }
  }, [isOpen])

  const renderOptions = useCallback(
    (options: ComboboxOptions, showCheckmark: boolean) => {
      return options.map((option, index) => {
        if (isOptionGroupGuard(option)) {
          return (
            <HeaderOption
              key={`${name}-${option.name}-fragment`}
              option={option}>
              {renderOptions(option.options, showCheckmark)}
            </HeaderOption>
          )
        }

        if (optionIdsToOmit.includes(option.id)) {
          return <></>
        }
        return (
          <ComboboxHeadlessUI.Option
            key={`${name}-${option.id}`}
            disabled={option.id === null && hideDefaultOption}
            className={({ active }) =>
              classNames(
                'relative cursor-default select-none py-2 pr-4',
                'rounded-md text-gray-500 dark:text-gray-200',
                {
                  'bg-info-lighten dark:bg-gray-600': active,
                  hidden: option.id === null && hideDefaultOption,
                },
                showCheckmark && 'pl-10',
                optionWrapperClassName,
              )
            }
            value={option}>
            {({ selected, active }) => {
              return (
                <ComboboxItem
                  active={active}
                  setHovered={setHovered}
                  index={index}>
                  <Option
                    active={active}
                    selected={selected}
                    option={option}
                    OptionComponent={OptionComponent}
                  />
                  {showCheckmark && (
                    <SelectedCheckmark selected={selected} active={active} />
                  )}
                </ComboboxItem>
              )
            }}
          </ComboboxHeadlessUI.Option>
        )
      })
    },
    [OptionComponent, hideDefaultOption, name, setHovered],
  )

  // If initial value is set to null, then set selected to
  // the first item in the options.
  useEffect(() => {
    if (options.length && !selected) {
      setSelected(options[0])
    }
  }, [options, selected, setSelected])

  const fallback = useMemo(
    () => (
      <Select
        {...{ id, setSelected, selected, options, label, name, selectRef }}
        className={selectClasses}
        optionValue={fallBackOptionValue}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    ),
    [
      fallBackOptionValue,
      id,
      label,
      name,
      onBlur,
      onFocus,
      options,
      selectClasses,
      selectRef,
      selected,
      setSelected,
    ],
  )

  const noItemsFound = useMemo(
    () => Boolean(search) && !options.length,
    [options.length, search],
  )

  const emptyList = useMemo(
    () => !Boolean(_options?.length),
    [_options?.length],
  )

  return isSSR || isTouchDevice ? (
    fallback
  ) : (
    <>
      <ComboboxHeadlessUI
        disabled={disabled}
        value={selected}
        onChange={setSelected}>
        {({ open }) => {
          const listboxClasses = classNames(selectClasses, {
            'ring-2 ring-info/50 border-info': open,
            'bg-gray-50 dark:bg-gray-700': disabled,
          })

          return (
            <ComboboxItems
              setIsOpen={setIsOpen}
              open={open}
              className={classNames('text-base', {
                relative,
                'mt-1': label,
              })}>
              {label !== null && (
                <ComboboxHeadlessUI.Label
                  className={classNames(
                    'block text-xs font-bold uppercase text-gray-400 dark:text-gray-200',
                    labelClassName,
                  )}>
                  {label}
                </ComboboxHeadlessUI.Label>
              )}
              <div ref={refs.setReference} {...getReferenceProps()}>
                <div
                  className={classNames(listboxClasses, {
                    hidden: !open || !showSearch,
                  })}>
                  <ComboboxHeadlessUI.Input
                    ref={inputRef}
                    onKeyDown={(e) => {
                      // if there are components above this that listen to onKeydown,
                      // bubbling this up could cause the parent component to rerender
                      // the combobox, specifically when pressing keys like "shift" and "ctrl"
                      e.stopPropagation()
                    }}
                    // what to show when the input box is remounted.
                    // essentially clears the input when the combobox is opened and closed
                    displayValue={() => ''}
                    type="search"
                    placeholder={searchPlaceholder}
                    className={classNames(
                      'left-0 block max-h-60 w-full overflow-auto pt-0.5 focus:outline-none dark:border-gray-700 dark:bg-gray-500',
                      searchPlaceholderClassName,
                    )}
                    onChange={(event) => {
                      setSearch(event.target.value)
                    }}
                  />
                </div>

                <ComboboxHeadlessUI.Button
                  as={forwardRef(
                    (
                      props: ComponentProps<'button'>,
                      ref: Ref<HTMLButtonElement>,
                    ) => (
                      <button {...props} tabIndex={0} ref={ref} />
                    ),
                  )}
                  className={classNames(listboxClasses, {
                    hidden: open && showSearch,
                  })}
                  data-cy={cyDataValue}>
                  {isFunction(ButtonComponent) ? (
                    ButtonComponent({ id: selected?.id, options })
                  ) : (
                    <span
                      className={classNames(
                        'block truncate py-px dark:text-gray-50',
                        {
                          'text-gray-200 dark:text-gray-300':
                            selected?.id === null || selected === null,
                          'cursor-not-allowed select-none text-gray-400 dark:text-gray-400':
                            disabled,
                        },
                      )}>
                      {children
                        ? children(selected)
                        : selected === null
                        ? defaultValue || 'Select an option'
                        : selected?.value}
                    </span>
                  )}
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1">
                    {UnfoldComponent ? (
                      <UnfoldComponent disabled={disabled} open={open} />
                    ) : (
                      <UnfoldIcon
                        className={classNames(
                          'h-5 w-5 text-gray-300 dark:text-gray-200',
                          {
                            'animate-pulse cursor-not-allowed select-none text-gray-200 dark:text-gray-400':
                              disabled,
                          },
                        )}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </ComboboxHeadlessUI.Button>
              </div>
              {open && (
                <FloatingPortal>
                  <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    {...getFloatingProps()}>
                    <ComboboxHeadlessUI.Options
                      static
                      className="mb-1 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white p-2 text-base shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-500 sm:text-sm">
                      {emptyList && (
                        <li
                          className={classNames(
                            'relative cursor-default select-none py-2 pl-10 pr-4',
                            'rounded-md text-gray-500 dark:text-gray-200',
                          )}>
                          No items available.
                        </li>
                      )}
                      {!noItemsFound && renderOptions(options, showCheckmark)}
                      {noItemsFound && (
                        <li
                          className={classNames(
                            'relative cursor-default select-none py-2 pl-10 pr-4',
                            'rounded-md text-gray-500 dark:text-gray-200',
                            searchPlaceholderClassName,
                          )}>
                          Nothing matched "{search}"
                        </li>
                      )}
                    </ComboboxHeadlessUI.Options>
                  </div>
                </FloatingPortal>
              )}
            </ComboboxItems>
          )
        }}
      </ComboboxHeadlessUI>
      <input type="hidden" name={name} value={String(selected?.id)} />
    </>
  )
}
